import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  directoryApi,
  type PartyDocument,
  type PartyDocumentTypeValue,
  type UpdatePartyDocumentRequest,
} from '../client';

/**
 * Stable query-key factory for party-document caches. Keyed by `partyId`
 * so each party's document list owns its own slot.
 */
export const partyDocumentKeys = {
  all: ['directory', 'partyDocuments'] as const,
  byParty: (partyId: string) => ['directory', 'partyDocuments', 'party', partyId] as const,
  byId: (id: string) => ['directory', 'partyDocuments', 'id', id] as const,
};

/**
 * Fetch every document for a party. Pass `null` to keep the query disabled
 * (e.g. before the route param resolves or in create-mode of the Party
 * Details page where there's no party id yet).
 */
export function usePartyDocuments(
  partyId: string | null,
  options?: Omit<UseQueryOptions<PartyDocument[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<PartyDocument[], Error>({
    queryKey: partyId ? partyDocumentKeys.byParty(partyId) : partyDocumentKeys.all,
    queryFn: () => directoryApi.getPartyDocuments(partyId!),
    enabled: !!partyId,
    staleTime: 60 * 1000,
    ...options,
  });
}

/**
 * Upload a new document. Wraps the multipart POST. Caller passes the
 * party id alongside the file so cache invalidation can target the right
 * per-party list slot.
 */
export function useUploadPartyDocument() {
  const qc = useQueryClient();
  return useMutation<
    string,
    Error,
    {
      partyId: string;
      file: File;
      description: string | null;
      type: PartyDocumentTypeValue;
    }
  >({
    mutationFn: ({ partyId, file, description, type }) =>
      directoryApi.uploadPartyDocument(partyId, file, description, type),
    onSuccess: (_id, { partyId }) => {
      qc.invalidateQueries({ queryKey: partyDocumentKeys.byParty(partyId) });
    },
  });
}

/**
 * Update document metadata (description + type only). Caller passes
 * `partyId` so the per-party list refreshes after the PUT.
 */
export function useUpdatePartyDocument() {
  const qc = useQueryClient();
  return useMutation<
    string,
    Error,
    { body: UpdatePartyDocumentRequest; partyId: string }
  >({
    mutationFn: ({ body }) => directoryApi.updatePartyDocument(body),
    onSuccess: (_id, { body, partyId }) => {
      qc.invalidateQueries({ queryKey: partyDocumentKeys.byParty(partyId) });
      qc.invalidateQueries({ queryKey: partyDocumentKeys.byId(body.documentId) });
    },
  });
}

/**
 * Soft-delete a document. Blob stays in storage on the BE in case the
 * row needs to be restored; the per-party list refetches without it.
 */
export function useDeletePartyDocument() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { id: string; partyId: string }>({
    mutationFn: ({ id }) => directoryApi.deletePartyDocument(id),
    onSuccess: (_, { id, partyId }) => {
      qc.invalidateQueries({ queryKey: partyDocumentKeys.byParty(partyId) });
      qc.removeQueries({ queryKey: partyDocumentKeys.byId(id) });
    },
  });
}
